import * as React from "react";
import EmbedHeader from "./EmbedHeader";
import type { ILevel } from "../levels";
import videoImage from "src/assets/video-image.png";

interface IProps {
  level: ILevel;
  onContinue: () => void;
  onFinishToForm: () => void;
}

export default function LevelStep(props: IProps) {
  const { level, onFinishToForm } = props;
  const { videoSrc, posterSrc, externalVideoUrl, introBullets } = level;
  const poster = posterSrc ?? videoImage;
  const [videoLoadError, setVideoLoadError] = React.useState(false);
  const [videoStarted, setVideoStarted] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const hasIntroBullets = Boolean(introBullets?.length);

  // If the level changes, allow the new video to try loading again.
  React.useEffect(() => {
    setVideoLoadError(false);
    setVideoStarted(false);
  }, [videoSrc]);

  const handleThumbnailClick = () => {
    const v = videoRef.current;
    if (!v) {
      return;
    } 
    // User gesture -> allowed to call play on mobile.
    v.play().catch(() => {
      // Ignore play errors; we still show the thumbnail.
    });
  };

  return (
    <div className="c101-embed">
      <div className="c101-embed-shell">
        <EmbedHeader />
        <div className="c101-embed-rule" />

        <main className="c101-embed-main">
          {/* <p className="c101-embed-step-meta">
            Level {levelIndex} of {TOTAL_LEVELS}
          </p> */}
          <p className="c101-embed-category">{level.categoryLabel}</p>
          <h1 className="c101-embed-page-title">{level.pageTitle}</h1>
          <p className={`c101-embed-intro ${hasIntroBullets ? "c101-embed-intro--with-bullets" : ""}`}>
            {level.intro}
          </p>

          {hasIntroBullets ? (
            <ul className="c101-embed-bullets">
              {introBullets!.map((b, idx) => (
                <li key={idx}>{b}</li>
              ))}
            </ul>
          ) : null}

          <div className="c101-video-outer">
            {videoSrc && !videoLoadError ? (
              <div className="c101-video-frame c101-video-frame--16x9">
                <video
                  width="100%"
                  controls={true}
                  autoPlay={false}
                  muted={false}
                  preload="metadata"
                  poster={poster}
                  playsInline={true}
                  onError={() => setVideoLoadError(true)}
                  onPlay={() => setVideoStarted(true)}
                  ref={videoRef}
                >
                    <source src={videoSrc} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>

                {!videoStarted ? (
                  <div
                    className="c101-video-thumbnail"
                    onClick={handleThumbnailClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " "){
                        handleThumbnailClick();
                      }
                    }}
                  >
                    <img src={poster} alt="" className="c101-video-thumbnail-img" />
                    <div className="c101-video-play-overlay" aria-hidden={true}>
                      <span className="c101-video-play-icon" />
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="c101-video-frame c101-video-frame--poster">
                <img
                  src={posterSrc ?? videoImage}
                  alt=""
                  className="c101-video-poster-img"
                />
                <div className="c101-video-play-overlay" aria-hidden={true}>
                  <span className="c101-video-play-icon" />
                </div>
                {externalVideoUrl ? (
                  <a
                    className="c101-video-poster-hit"
                    href={externalVideoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="c101-sr-only">Open instruction video</span>
                  </a>
                ) : null}
              </div>
            )}
          </div>

          <div className="c101-embed-footer-row">
            <p className="c101-embed-footer-note">
              You&apos;ll be redirected to Grit Leadership to complete the survey.
              <br />
              Return to the Champions 101 Training Center once you&apos;re done.
            </p>
            <button
              type="button"
              className="c101-embed-cta"
              onClick={onFinishToForm}
            >
              <>
              Take me to the survey
                <span className="c101-embed-cta-arrow" aria-hidden={true}>
                  →
                </span>
              </>
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/features/home/pages/embed/components/LevelStep.tsx